import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";

function PasswordField({ id, label, value, onChange }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-slate-300">{label}</Label>
      <Input id={id} data-testid={`${id}-input`} type="password" required minLength={6} placeholder="At least 6 characters" value={value}
        onChange={(e) => onChange(e.target.value)} className="h-12 rounded-xl bg-[#0B0F19] border-[#26334D] focus-visible:ring-[#FF3B6B]" />
    </div>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [valid, setValid] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setValid(false); return; }
    api.get("/auth/reset-password/check", { params: { token } }).then((r) => setValid(r.data.valid)).catch(() => setValid(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setError("The two passwords don't match."); return; }
    setError(""); setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password updated — log in with your new password");
      navigate("/login");
    } catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  };

  return (
    <main className="max-w-md mx-auto px-4 py-16">
      <div className="rise rounded-3xl bg-[#151C2C] border border-[#26334D] p-8">
        <h1 className="text-3xl font-extrabold tracking-tight">Choose a new password</h1>
        {valid === null && <p data-testid="reset-checking" className="text-slate-400 text-sm mt-4">Checking your link…</p>}
        {valid === false && (
          <div data-testid="reset-invalid" className="mt-6 space-y-4">
            <p className="text-sm text-[#FF7A7A]">This reset link is invalid or has expired. Links work once, for 1 hour.</p>
            <Link to="/forgot-password" data-testid="reset-request-new" className="inline-flex h-11 px-6 items-center rounded-full bg-[#FF3B6B] font-semibold text-sm">Send me a new link</Link>
          </div>
        )}
        {valid && (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <PasswordField id="reset-password" label="New password" value={pw} onChange={setPw} />
            <PasswordField id="reset-confirm" label="Confirm new password" value={pw2} onChange={setPw2} />
            {error && <p data-testid="reset-error" className="text-sm text-[#FF7A7A]">{error}</p>}
            <Button data-testid="reset-submit-button" disabled={busy} type="submit" className="w-full h-12 rounded-full bg-[#FF3B6B] hover:bg-[#E02E5A] text-white font-semibold">
              {busy ? "Saving…" : "Save new password"}
            </Button>
            <p className="text-xs text-slate-500">For your security, you'll be signed out on all your devices.</p>
          </form>
        )}
      </div>
    </main>
  );
}
